import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useRef } from 'react';
import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import { 
  CheckCircle, 
  Home, 
  Building2, 
  Briefcase, 
  Stethoscope, 
  Store, 
  Bed,
  VolumeX,
  Star,
  MapPin,
  Navigation
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { HoverEffect } from '@/components/HoverEffect';
import { Timeline } from '@/components/Timeline';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { SEO } from "@/components/SEO";
import { trackWhatsAppClick } from "@/lib/gtm";

const DynamicTitle = () => {
  const words = ["barulho externo", "trânsito intenso", "som da cidade"];
  const containerRef = useRef<HTMLDivElement>(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const [activeIndex, setActiveIndex] = useState(0);

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    const start = 0.35;
    const end = 0.65;
    let p = (latest - start) / (end - start);
    p = Math.max(0, Math.min(1, p));
    const idx = Math.round(p * (words.length - 1));
    if (idx !== activeIndex) {
      setActiveIndex(idx);
    }
  });

  return (
    <div ref={containerRef} className="mb-4">
      <h2 className="text-xl md:text-2xl lg:text-4xl font-black text-foreground leading-tight flex flex-wrap justify-center items-center gap-x-2 md:gap-x-3 text-center px-4">
        <span className="text-foreground font-black">O</span>
        <span className="relative inline-flex flex-col h-[1.3em] overflow-hidden text-[#1c6874] font-black">
          <div 
            className="flex flex-col transition-transform duration-500 cubic-bezier(0.23, 1, 0.32, 1) absolute top-0 left-0 w-full"
            style={{ transform: `translateY(-${activeIndex * 1.3}em)` }}
          >
            {words.map((word, i) => (
              <span 
                key={i} 
                className={cn(
                  "h-[1.3em] flex items-center justify-center transition-opacity duration-300 whitespace-nowrap font-black",
                  i === activeIndex ? "opacity-100 scale-100" : "opacity-10 scale-90"
                )}
              >
                {word}
              </span>
            ))}
          </div>
          <span className="invisible pointer-events-none px-1 whitespace-nowrap">
            barulho externo
          </span>
        </span>
        <span className="w-full font-black mt-2">não precisa fazer parte da sua rotina</span>
      </h2>
    </div>
  );
};

export const Route = createFileRoute("/")({
  component: LandingPage,
});

function LandingPage() {
  const carouselImages = [
    { url: '/carousel-1.webp', alt: 'Ambiente Residencial' },
    { url: '/carousel-2.webp', alt: 'Quarto Silencioso' },
    { url: '/carousel-3.webp', alt: 'Sala de Estar' },
  ];

  const solutions = [
    { 
      title: 'Residências', 
      subtitle: 'Mais silêncio e conforto para o dia a dia da sua casa.',
      icon: Home,
      image: '/residencia-v2.webp'
    },
    { 
      title: 'Apartamentos', 
      subtitle: 'Reduza ruídos da rua, trânsito, vizinhança e áreas movimentadas.',
      icon: Building2,
      image: '/apartamento-v2.webp'
    },
    { 
      title: 'Escritórios', 
      subtitle: 'Mais concentração, privacidade e conforto acústico no ambiente de trabalho.',
      icon: Briefcase,
      image: '/escritorio-v2.webp'
    },
    { 
      title: 'Consultórios', 
      subtitle: 'Ambientes mais silenciosos para atendimento, conversa e concentração.',
      icon: Stethoscope,
      image: '/consultorio-v2.webp'
    },
    { 
      title: 'Ambientes comerciais', 
      subtitle: 'Soluções acústicas para melhorar a experiência de clientes e equipes.',
      icon: Store,
      image: '/comercio-v2.webp'
    },
    { 
      title: 'Quartos', 
      subtitle: 'Mais silêncio e conforto para o seu descanso.',
      icon: Bed,
      image: '/quartos.webp'
    },
  ];

  const products = [
    {
      title: 'Janela Sobreposta',
      description: 'Solução ideal para reduzir ruídos externos sem precisar trocar a janela já existente. Instalada sobre a estrutura atual, cria uma barreira acústica eficiente com acabamento moderno e instalação limpa.',
      benefit: 'Redução de ruídos sem trocar a janela atual.',
      image: '/lovable-uploads/0ff3abc7-93e4-4d15-8c3e-52f0baad18f7.webp',
      buttonLabel: 'Quero essa solução',
      buttonHref: '/orcamento'
    },
    {
      title: 'Janela de correr acústica',
      description: 'Opção prática e elegante para quem busca conforto acústico, boa vedação e aproveitamento de espaço.',
      benefit: 'Praticidade e isolamento eficiente.',
      image: '/janela-correr-v5.webp',
      buttonLabel: 'Quero essa solução',
      buttonHref: '/orcamento'
    },
    {
      title: 'Janela com persiana integrada',
      description: 'Solução que combina controle de luminosidade, privacidade e isolamento acústico em um único sistema.',
      benefit: 'Controle de luz e conforto acústico.',
      image: '/produto02.webp',
      buttonLabel: 'Quero essa solução',
      buttonHref: '/orcamento'
    },
    {
      title: 'Janela Maxim-Ar',
      description: 'Modelo funcional e sofisticado, muito utilizado em corredores, áreas sociais e ambientes que precisam de ventilação com boa vedação.',
      benefit: 'Vedação reforçada e estética preservada.',
      image: '/janela-maxim-ar-acustica.webp',
      buttonLabel: 'Quero essa solução',
      buttonHref: '/orcamento'
    },
    {
      title: 'Janela de Giro',
      description: 'Ideal para quem busca alto desempenho acústico e abertura ampla, com vedação por compressão e instalação rápida.',
      benefit: 'Máxima performance acústica.',
      image: '/produto04.webp',
      buttonLabel: 'Quero essa solução',
      buttonHref: '/orcamento'
    },
  ];

  const benefits = [
    { 
      title: 'Redução de ruídos externos', 
      desc: 'Soluções desenvolvidas para diminuir incômodos causados por trânsito, obras, bares, vizinhança e áreas movimentadas no Rio de Janeiro.' 
    },
    { 
      title: 'Vedação eficiente', 
      desc: 'Projetos com foco em melhor isolamento acústico, encaixe adequado e acabamento profissional.' 
    },
    { 
      title: 'Soluções sob medida', 
      desc: 'Janelas e esquadrias acústicas personalizadas para diferentes tipos de ambientes residenciais e comerciais.' 
    },
    { 
      title: 'Atendimento no RJ', 
      desc: 'Atendemos clientes em todo o Rio de Janeiro, com fábrica localizada em Duque de Caxias.' 
    },
  ];

  const steps = [
    { title: 'Contato', desc: 'Você entra em contato com a nossa equipe.' },
    { title: 'Entendimento', desc: 'Analisamos sua necessidade, ambiente e tipo de ruído.' },
    { title: 'Proposta', desc: 'Criamos uma solução personalizada para o seu espaço.' },
    { title: 'Fabricação', desc: 'Produzimos as esquadrias sob medida com materiais de alta qualidade.' },
    { title: 'Instalação', desc: 'Realizamos a instalação de forma rápida, limpa e eficiente.' },
  ];

  return (
    <>
      <SEO 
        title="Janelas e esquadrias acústicas no Rio de Janeiro | STOP SOM"
        description="A STOP SOM Janelas Acústicas oferece janelas e esquadrias acústicas sob medida para reduzir ruídos externos em casas, apartamentos e empresas no Rio de Janeiro. Fábrica em Duque de Caxias - RJ."
      />
      <div className="bg-white">
      {/* Hero */}
      <section id="inicio" className="relative min-h-[calc(100vh-80px)] flex items-center pt-24 pb-8 lg:pt-32 lg:pb-16 px-6 overflow-hidden">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column: Text Content */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.8 }} 
              className="z-10 text-center lg:text-left"
            >
              <h1 className="text-2xl md:text-4xl lg:text-4xl xl:text-5xl font-extrabold text-foreground leading-[1.2] lg:leading-[1.1] mb-6 tracking-tight">
                Janelas e esquadrias acústicas no <span className="text-secondary italic text-nowrap relative inline-block">Rio de Janeiro <span className="absolute -bottom-1 left-0 w-full h-2 bg-secondary/10 -rotate-1" /></span>
              </h1>
              <p className="text-lg lg:text-xl font-normal text-gray-600 mb-6 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                Soluções sob medida para reduzir ruídos externos e levar mais conforto para casas, apartamentos, escritórios e ambientes comerciais em todo o RJ.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento." 
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick("Solicitar orçamento", "https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento.", "hero")}
                  data-gtm-event="whatsapp_click"
                  data-conversion="whatsapp"
                  data-button-location="hero"
                  className="bg-primary text-white px-6 md:px-8 py-4 rounded-xl font-semibold text-base md:text-lg hover:bg-secondary transition-all transform hover:-translate-y-1 shadow-xl shadow-primary/20 flex items-center justify-center gap-2"
                >
                  <WhatsAppIcon className="w-6 h-6" /> Solicitar orçamento
                </a>
                <Link to="/produtos" className="bg-white border-2 border-primary text-primary px-6 md:px-8 py-4 rounded-xl font-semibold text-base md:text-lg hover:bg-primary/5 transition-all flex items-center justify-center">
                  Conhecer soluções
                </Link>
              </div>
            </motion.div>

            {/* Right Column: Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="relative aspect-[4/3] lg:aspect-square xl:aspect-[4/3] w-full max-w-2xl mx-auto lg:ml-auto"
            >
              <div className="absolute inset-0 bg-accent/10 rounded-[2.5rem] -rotate-3 transform" />
              <img 
                src="/lovable-uploads/9f4b4585-fd8c-421d-a339-bbf43d965426.webp" 
                alt="Ambiente residencial moderno com janelas premium" 
                width="800"
                height="600"
                className="relative w-full h-full object-cover rounded-[2.5rem] shadow-2xl border-4 border-white"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-2xl shadow-xl hidden xl:block">
                <div className="flex items-center gap-3">
                  <div className="bg-secondary/10 p-2 rounded-lg text-secondary">
                    <VolumeX size={24} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-primary">Janela Sobreposta</p>
                    <p className="text-xs text-gray-500 font-medium">Redução de ruídos sem trocar a janela atual</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* Background Elements */}
        <div className="absolute top-1/2 left-0 -translate-y-1/2 -z-10 w-64 h-64 bg-primary/5 blur-[120px] rounded-full" />
        <div className="absolute bottom-0 right-0 -z-10 w-96 h-96 bg-accent/5 blur-[150px] rounded-full" />
      </section>

      {/* Dores */}
      <section className="py-12 md:py-24 bg-white px-6 overflow-hidden">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <DynamicTitle />
            <p className="text-gray-500 font-normal max-w-2xl mx-auto">
              Identifique o tipo de ruído que mais afeta seu conforto e descubra como uma solução acústica sob medida pode transformar seu ambiente.
            </p>
          </div>
          
          <HoverEffect 
            items={[
              { title: "Trânsito intenso", description: "Reduza ruídos de carros, motos e avenidas próximas em seu lar." },
              { title: "Obras constantes", description: "Tenha mais conforto mesmo com reformas e construções ao redor." },
              { title: "Comércio ruidoso", description: "Minimize sons de lojas, bares e fluxo constante do seu bairro." },
              { title: "Sono prejudicado", description: "Crie um ambiente mais calmo para dormir melhor e viver melhor." },
              { title: "Foco prejudicado", description: "Melhore a concentração em estudos, reuniões e trabalho remoto." },
              { title: "Descanso afetado", description: "Transforme o espaço em um refúgio mais silencioso e agradável." },
            ]}
          />
        </div>
      </section>

      {/* Soluções */}
      <section id="solucoes" className="py-16 pb-32 md:py-24 px-6 bg-[#F8FAFC] overflow-hidden">
        <div className="container mx-auto max-w-[1280px]">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">Soluções para cada ambiente</h2>
            <p className="text-gray-500 font-normal text-lg max-w-2xl mx-auto">Desenvolvemos projetos de janelas e esquadrias sob medida para reduzir ruídos externos e melhorar o conforto de diferentes tipos de espaço em todo o Rio de Janeiro.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-6 lg:gap-4">
            {solutions.map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden rounded-[24px] bg-white cursor-pointer shadow-sm hover:shadow-xl transition-all duration-300 w-full h-auto flex flex-col lg:aspect-[4/3] lg:max-h-[280px] border border-slate-100 lg:border-none"
              >
                {/* Image Container */}
                <div className="relative w-full aspect-[16/9] lg:absolute lg:inset-0 lg:h-full lg:aspect-auto overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.title} 
                    width="400"
                    height="300"
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 lg:group-hover:scale-105" 
                  />
                  {/* Overlay - Desktop Only */}
                  <div className="hidden lg:block absolute inset-0 bg-black/10 group-hover:bg-black/60 transition-all duration-500" />
                </div>
                
                {/* Content Overlay - Desktop Only */}
                <div className="hidden lg:flex absolute inset-0 flex-col items-center justify-center p-6 text-white text-center">
                  <div className="transform transition-all duration-500 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 flex flex-col items-center">
                    <div className="text-[#45c8bf] mb-4">
                      {item.icon && <item.icon size={42} strokeWidth={1.5} />}
                    </div>
                    
                    <h3 className="text-xl md:text-2xl font-bold mb-2 font-sans tracking-tight">{item.title}</h3>
                    
                    <div className="w-10 h-0.5 bg-[#45c8bf] mb-4 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 delay-100" />
                    
                    <p className="text-sm md:text-base opacity-90 font-sans leading-snug max-w-[240px]">
                      {item.subtitle}
                    </p>
                  </div>
                </div>

                {/* Content Below - Mobile/Tablet Only */}
                <div className="lg:hidden flex flex-col p-6 bg-white border-t border-slate-50 flex-1">
                  <div className="text-[#1c6874] mb-3">
                    {item.icon && <item.icon size={28} strokeWidth={2} />}
                  </div>
                  
                  <h3 className="text-lg font-bold mb-2 text-foreground font-sans tracking-tight">
                    <span className="bg-slate-100 px-3 py-1 rounded-lg inline-block">{item.title}</span>
                  </h3>
                  
                  <p className="text-sm text-gray-500 font-sans leading-relaxed">
                    {item.subtitle}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Produtos */}
      <Timeline 
        data={products} 
        title="Janelas e esquadrias acústicas sob medida no RJ" 
        subtitle="Conheça soluções desenvolvidas para reduzir ruídos externos, melhorar a vedação e trazer mais conforto para diferentes tipos de ambientes no Rio de Janeiro."
      />

      {/* Benefícios */}
      <section id="beneficios" className="py-12 md:py-24 px-6 bg-primary text-white overflow-hidden relative">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-[100px]" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-accent rounded-full blur-[120px]" />
        </div>
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="text-center lg:text-left bg-white/5 lg:bg-transparent p-8 md:p-12 lg:p-0 rounded-[3rem] lg:rounded-none">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-8 leading-tight">Por que escolher as janelas da STOP SOM?</h2>
              <p className="text-xl opacity-80 font-normal mb-12 leading-relaxed">Combinamos tecnologia, acabamento e atendimento personalizado para entregar soluções acústicas eficientes para cada ambiente.</p>
              <div className="grid sm:grid-cols-2 gap-8">
                {benefits.map((benefit, i) => (
                  <div key={i} className="flex items-start justify-center lg:justify-start gap-4">
                    <div className="bg-accent rounded-full p-1 text-primary shrink-0 mt-1">
                      <CheckCircle size={20} fill="currentColor" className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg md:text-xl mb-1">{benefit.title}</h3>
                      <p className="text-sm opacity-80 leading-relaxed">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square bg-white/10 rounded-[3rem] p-8 backdrop-blur-sm border border-white/20">
                <div className="w-full h-full bg-gray-200 rounded-2xl overflow-hidden shadow-2xl">
                  <img src="/por-que-escolher.webp" alt="Por que escolher STOP SOM" width="600" height="600" loading="lazy" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-accent text-primary p-6 rounded-3xl shadow-xl hidden md:block">
                <div className="flex items-center gap-3">
                  <Star fill="currentColor" />
                  <div>
                    <p className="text-2xl font-bold">100%</p>
                    <p className="text-sm font-semibold uppercase tracking-tighter leading-none">Satisfação</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seção de Destaque */}
      <section className="py-12 md:py-24 px-6 bg-white overflow-hidden">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2 text-center lg:text-left">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-6">Mais silêncio para viver, trabalhar e descansar melhor</h2>
              <p className="text-xl text-gray-800 font-semibold leading-relaxed mb-8">
                A STOP SOM cria soluções personalizadas para transformar ambientes afetados pelo trânsito intenso <br className="hidden lg:block" /> e ruídos externos em espaços mais confortáveis, tranquilos e funcionais.
              </p>
              <div className="mt-4 flex justify-center lg:justify-start">
                <a 
                  href="https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento." 
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick("Solicitar projeto personalizado", "https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento.", "destaque")}
                  data-gtm-event="whatsapp_click"
                  data-conversion="whatsapp"
                  data-button-location="destaque"
                  className="inline-flex items-center gap-2 bg-primary text-white px-8 md:px-10 py-4 rounded-2xl font-bold text-base md:text-xl hover:bg-secondary transition-all transform hover:-translate-y-1 shadow-xl shadow-primary/20"
                >
                  <WhatsAppIcon className="w-6 h-6" /> Solicitar projeto personalizado
                </a>
              </div>
            </div>
            <div className="lg:w-1/2">
              <div className="relative rounded-[3rem] overflow-hidden shadow-2xl">
                <Carousel opts={{ loop: true }} plugins={[Autoplay({ delay: 5000 })]} className="w-full h-full">
                  <CarouselContent>
                    {carouselImages.map((image, index) => (
                      <CarouselItem key={index} className="basis-full">
                        <div className="relative aspect-[4/3] lg:aspect-auto lg:h-[500px]">
                          <img src={image.url} alt={image.alt} width="800" height="600" loading="lazy" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-primary/10" />
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <div className="absolute bottom-6 right-6 flex gap-2 z-10">
                    <CarouselPrevious className="static translate-y-0 h-10 w-10 border-white/20 bg-white/10 text-white hover:bg-white hover:text-primary backdrop-blur-md transition-colors" />
                    <CarouselNext className="static translate-y-0 h-10 w-10 border-white/20 bg-white/10 text-white hover:bg-white hover:text-primary backdrop-blur-md transition-colors" />
                  </div>
                </Carousel>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Airbnb / Imóveis de Temporada */}
      <section className="py-12 md:py-24 px-6 bg-[#F8FAFC] overflow-hidden">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            {/* Coluna Esquerda: Texto e Benefícios */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:w-1/2 order-1 lg:order-1"
            >
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-6 leading-tight">
                Mais conforto para hóspedes.<br />Mais valor para seu imóvel.
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                As janelas acústicas sobrepostas ajudam a reduzir ruídos externos sem grandes obras, tornando apartamentos, studios e imóveis de temporada mais silenciosos, confortáveis e bem avaliados.
              </p>
              
              {/* Badges/Pílulas */}
              <div className="flex flex-wrap gap-3 mb-10">
                {[
                  "Sem grandes obras",
                  "Instalação rápida",
                  "Mais conforto acústico",
                  "Ideal para Airbnb"
                ].map((benefit, index) => (
                  <span 
                    key={index}
                    className="px-4 py-2 bg-white border border-secondary/20 text-primary rounded-full text-sm font-semibold shadow-sm"
                  >
                    {benefit}
                  </span>
                ))}
              </div>
              
              <div className="flex justify-center lg:justify-start">
                <a 
                  href="https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Quero%20melhorar%20meu%20im%C3%B3vel%20de%20temporada." 
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick("Quero melhorar meu imóvel", "https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Quero%20melhorar%20meu%20im%C3%B3vel%20de%20temporada.", "airbnb")}
                  data-gtm-event="whatsapp_click"
                  data-conversion="whatsapp"
                  data-button-location="airbnb"
                  className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-secondary transition-all transform hover:-translate-y-1 shadow-lg shadow-primary/20"
                >
                  <WhatsAppIcon className="w-6 h-6" /> Quero melhorar meu imóvel
                </a>
              </div>
            </motion.div>
            
            {/* Coluna Direita: Imagens */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:w-1/2 order-2 lg:order-2 w-full relative"
            >
              <div className="relative aspect-[4/3] rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white z-10">
                <img 
                  src="/lovable-uploads/c98fcb7b-3c55-4f2a-b551-6995322f2428.webp" 
                  alt="Apartamento moderno de temporada com janela acústica" 
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent pointer-events-none" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Como Funciona */}

      <section id="como-funciona" className="py-12 md:py-24 px-6 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">Como funciona nosso atendimento no Rio de Janeiro</h2>
            <div className="h-1.5 w-24 bg-accent mx-auto rounded-full" />
          </div>
          <div className="relative">
            <div className="hidden lg:block absolute top-1/2 left-0 w-full h-0.5 bg-gray-100 -translate-y-1/2" />
            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-12 relative z-10">
              {steps.map((step, i) => (
                <div key={i} className="flex flex-col items-center text-center group">
                  <div className="w-16 h-16 rounded-2xl bg-white border-2 border-primary text-primary flex items-center justify-center font-bold text-2xl mb-6 group-hover:bg-primary group-hover:text-white transition-all transform group-hover:-translate-y-2 group-hover:shadow-xl group-hover:shadow-primary/20">
                    {i + 1}
                  </div>
                  <h3 className="font-semibold text-xl mb-3">{step.title}</h3>
                  <p className="text-gray-500 font-normal text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sobre a STOP SOM */}
      <section id="sobre-nos" className="py-12 md:py-24 px-6 bg-gray-50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto bg-white rounded-[3rem] p-10 lg:p-20 shadow-xl flex flex-col md:flex-row gap-12 items-center">
            <div className="md:w-1/3 flex justify-center">
              <img src="/logo.png" alt="STOP SOM Logo" width="280" height="100" loading="lazy" className="w-full h-auto object-contain max-w-[280px]" />
            </div>
            <div className="md:w-2/3 text-center md:text-left">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-6">Especialistas em janelas e esquadrias acústicas</h2>
              <p className="text-lg text-gray-600 font-normal leading-relaxed mb-6">
                A STOP SOM Janelas Acústicas é especializada em janelas e esquadrias acústicas sob medida para quem busca mais silêncio, conforto e privacidade. Atendemos clientes em todo o Rio de Janeiro, com fábrica em Duque de Caxias e soluções para residências, apartamentos, escritórios, consultórios e ambientes comerciais.
              </p>
              <div className="flex flex-wrap justify-center md:justify-start gap-4">
                <div className="border border-[#3da29f]/20 text-[#1c6874] bg-slate-100 md:bg-white/70 rounded-full px-[18px] py-[10px] font-semibold hover:bg-white transition-colors cursor-default shadow-sm">
                  Confiança
                </div>
                <div className="border border-[#3da29f]/20 text-[#1c6874] bg-slate-100 md:bg-white/70 rounded-full px-[18px] py-[10px] font-semibold hover:bg-white transition-colors cursor-default shadow-sm">
                  Qualidade
                </div>
                <div className="border border-[#3da29f]/20 text-[#1c6874] bg-slate-100 md:bg-white/70 rounded-full px-[18px] py-[10px] font-semibold hover:bg-white transition-colors cursor-default shadow-sm">
                  Projeto sob medida
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Atendimento Local e NAP */}
      <section className="py-12 md:py-24 px-6 bg-white overflow-hidden border-t border-gray-100">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-6">Atendimento em janelas e esquadrias acústicas no Rio de Janeiro</h2>
              <p className="text-lg text-gray-600 font-normal leading-relaxed mb-6">
                A STOP SOM Janelas Acústicas atende clientes em todo o Rio de Janeiro com soluções sob medida para reduzir ruídos externos e melhorar o conforto de residências, apartamentos, escritórios e ambientes comerciais. Nossa fábrica está localizada em Duque de Caxias - RJ.
              </p>
              <p className="text-lg text-gray-600 font-normal leading-relaxed">
                Nossas esquadrias antirruído e janelas sob medida são projetadas para entregar o máximo isolamento acústico, garantindo silêncio e tranquilidade para o seu descanso ou trabalho em qualquer região do RJ.
              </p>
            </div>
            
            <div className="bg-gray-50 p-8 lg:p-12 rounded-[3rem] border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full translate-x-1/2 -translate-y-1/2" />
              
              <h3 className="text-2xl font-bold text-primary mb-8 relative z-10">Visite ou fale com a STOP SOM</h3>
              
              <div className="space-y-6 relative z-10 mb-10">
                <div className="flex items-start gap-4">
                  <div className="bg-primary p-3 rounded-xl text-white shrink-0">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg text-primary">STOP SOM Janelas Acústicas</h4>
                    <p className="text-gray-600">Fábrica: Rua Itatinga, 511 — Duque de Caxias - RJ</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-secondary p-3 rounded-xl text-white shrink-0">
                    <WhatsAppIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg text-primary">Telefone e WhatsApp</h4>
                    <a href="tel:+5521992698354" className="text-gray-600 hover:text-secondary transition-colors">(21) 99269-8354</a>
                  </div>
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4 relative z-10">
                <a 
                  href="https://maps.google.com/?q=Rua+Itatinga,+511+Duque+de+Caxias+RJ" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-primary text-white px-6 py-4 rounded-xl font-bold hover:bg-secondary transition-all shadow-lg shadow-primary/20"
                >
                  <Navigation size={20} /> Como chegar à fábrica
                </a>
                <a 
                  href="https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento." 
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick("Falar no WhatsApp", "https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento.", "contato")}
                  data-gtm-event="whatsapp_click"
                  data-conversion="whatsapp"
                  data-button-location="contato"
                  className="flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-4 rounded-xl font-bold hover:bg-[#128C7E] transition-all shadow-lg shadow-green-500/20"
                >
                  <WhatsAppIcon className="w-5 h-5" /> Falar no WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* JSON-LD LocalBusiness */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "STOP SOM Janelas Acústicas",
          "image": "https://stopsomesquadrias.com.br/logo.png",
          "@id": "https://stopsomesquadrias.com.br",
          "url": "https://stopsomesquadrias.com.br",
          "telephone": "+55 21 99269-8354",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Rua Itatinga, 511",
            "addressLocality": "Duque de Caxias",
            "addressRegion": "RJ",
            "postalCode": "25000-000",
            "addressCountry": "BR"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": -22.785,
            "longitude": -43.312
          },
          "areaServed": {
            "@type": "State",
            "name": "Rio de Janeiro"
          },
          "description": "Empresa especializada em janelas acústicas, esquadrias acústicas e soluções antirruído sob medida no Rio de Janeiro, com fábrica em Duque de Caxias - RJ.",
          "keywords": "janelas acústicas no Rio de Janeiro, esquadrias acústicas no Rio de Janeiro, esquadrias antirruído, janelas antirruído, esquadrias sob medida, fábrica em Duque de Caxias"
        })}
      </script>


      <section className="py-12 md:py-24 px-6 bg-white overflow-hidden">
        <div className="container mx-auto">
          <div className="bg-primary rounded-[3rem] p-12 lg:p-24 relative overflow-hidden text-center text-white">
            <div className="absolute top-0 right-0 w-1/3 h-full bg-accent/20 skew-x-12 translate-x-1/2" />
            <div className="relative z-10">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-8">Pronto para recuperar o silêncio do seu ambiente?</h2>
              <p className="text-xl opacity-80 font-normal mb-12 max-w-2xl mx-auto leading-relaxed">Fale com a STOP SOM e receba uma solução personalizada para o seu espaço.</p>
              <a 
                href="https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento." 
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick("Chamar no Whatsapp", "https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento.", "cta-final")}
                data-gtm-event="whatsapp_click"
                data-conversion="whatsapp"
                data-button-location="cta-final"
                className="inline-flex items-center gap-3 bg-accent text-primary px-8 md:px-12 py-5 md:py-6 rounded-2xl font-bold text-lg md:text-2xl hover:bg-white transition-all transform hover:scale-105 shadow-2xl shadow-black/20"
              >
                Chamar no Whatsapp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
